import { CustomParams, ExportType, MultiQueryResults } from "../../types";
import { log, dom } from "@omegagrid/core";
import { Dependency } from "../dependencyLoader";
import { ExtensionController } from "./extensionController";
import { LegacyAdapter, ExtensionV2Window } from "./legacyAdapter";
import rp from '@reporting/rp-app-host/src/global';
import { BroadcastEvent } from '@reporting/rp-app-host/src/container/broadcast';
import { Engine } from "../engine";

const logger = () => log.getLogger('rpex');

export class HtmlAdapterV2 extends ExtensionController {

	private _adapter: LegacyAdapter;
	get adapter() { return this._adapter }

	private _data: MultiQueryResults = new Map();
	get data() { return this._data }

	private _initialized = false;
	get isInitialized() { return this._initialized }

	private _error: string;
	get error() { return this._error }

	private _onBroadcast = (e: BroadcastEvent) => {
		const args = {message: e.message?.content};
		this.adapter.fire('broadcast', args);
		this.adapter.fire('receiveBroadcast', args);
	}

	constructor(engine: Engine) {
		super(engine);
		this.engine.addEventListener('broadcast', this._onBroadcast);
	}

	[Symbol.dispose](): void|Promise<void> {
		this.engine.removeEventListener('broadcast', this._onBroadcast);
	}

	getExportTypes(): ExportType[] {
		return [];
	}

	setupLegacyAdapter() {
		const win = this.engine.window as ExtensionV2Window;
		if (!win) return;
		// a fresh subclass per page: legacy extensions patch Reporting.Custom.prototype
		// (label, loader, ...) - the extension page owns its window, nothing leaks out
		win.Reporting = {
			Custom: class extends LegacyAdapter {},
			container: rp,
		};
		this.engine.eval(`
			window.rc = new Reporting.Custom();
			window.rp = Reporting.container;
		`); // force adapter to be created in the page scope the extension scripts run in
		this._adapter = win.rc;

		this._adapter.engine = this.engine;
		this._adapter.params = this.engine.parameters;
		this._adapter.registry.links['resource'] = rp.ms('extensions').createUrl(`${this.engine.id}/resourceContent/<resource>`);
		this._adapter.registry.links['file'] = rp.ms('definition').createUrl(`files/<file>/download`);
		this._adapter.registry.links['export'] = rp.ms('export').createUrl('/');
		this._adapter.registry.links['exec'] = rp.ms('extensions').createUrl(`${this.engine.id}/exec/<thread>`, {json: 1});

		return this.adapter;
	}

	modifyDependencies(dependencies: Dependency[]): Dependency[] {
		let modifiedDependencies = dependencies.map(d => d);

		let injectEjs = false;
		dependencies.forEach((d, i) => {
			if (/ejs(\.min)?\.js/.test(d.url)) {
				injectEjs = true;
				modifiedDependencies.splice(i, 1);
			} else if (/lodash(\.min)?\.js/.test(d.url)) {
				injectEjs = true;
			}
		});

		// use local version of ejs
		if (injectEjs) {
			modifiedDependencies.unshift({
				url: `${this.engine.options.staticUrl}/libs/ejs.min.js`,
				name: 'ejs',
				type: 'js'
			});
		}

		const localDepPredicate = (name: string) => !name.endsWith('.filter.js') && (name.endsWith('.js') || name.endsWith('.css'));

		// add local extension resources
		let localDependencies = this.engine.extension.resources.filter(r => localDepPredicate(r.name)).map(r => {
			const url = this.engine.ms.createUrl(`${this.engine.id}/resourceContent/${r.name}`);
			return {url, name: r.name, type: r.name.endsWith('.js') ? 'js' : 'css'};
		}) as Dependency[];

		const localBundle = localDependencies.find(d => d.name == 'build/bundle.js');
		
		// ignore all other js files if project contains bundle.js
		if (localBundle) localDependencies = [localBundle];

		return [...modifiedDependencies, ...localDependencies];
	}

	async beforeDependencies() {
		if (!this.engine.dataLoaded) {
			try	{
				await this.engine.loadResourceData();
			} catch (error) {
				if ((error as Error).message) {
					this._error = (error as Error).message;
				} else {
					this._error = (error as Error).toString();
				}
			}
		}
		this.setupLegacyAdapter();
	}

	/**
	 * The main template content changed (editor preview push) - update the
	 * legacy adapter's template mirror so the next render uses the new content.
	 * A null template is left alone: the extension is holding rendering
	 * suppressed and will restore its own copy when it is ready.
	 */
	onMainResourceChange(content: string) {
		if (this._adapter && this._adapter.template != null) {
			this._adapter.template = content;
		}
	}

	async refresh() {
		const template = await this.engine.getMainResource();
		this.render(this.engine.containerElement, template, this.engine.parameters);
	}

	async render(target: HTMLElement, template: string, data?: CustomParams): Promise<void> {
		if (!this.isInitialized) {
			this._initialized = true;
			this.adapter.template ??= template; // expose the template to the extension before init
			this.adapter.fire('init');
		}

		// legacy API contract: template set to null suppresses rendering - the extension
		// restores the template and calls refresh() itself once it is ready (e.g. after
		// loading its locale files in the 'init' handler)
		if (this.adapter.template === null) return;

		dom.empty(target);

		if (this._error) {
			// JSON.stringify - sql error messages carry newlines and backslashes
			this.engine.eval(`window.rc.error(${JSON.stringify(this._error)})`);
			return;
		}

		this.adapter.renderArgs = {template: this.adapter.template ?? template, data, target};
		this.engine.eval('window.rc.invokeRender()');

		this.adapter.fire('refresh');
	}

}
